import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider } from './components/Auth/AuthProvider';
import { Dashboard } from './components/Dashboard/Dashboard';
import { LoginForm } from './components/Auth/LoginForm';
import { SignupForm } from './components/Auth/SignupForm';
import { LandingPage } from './components/Landing/LandingPage';
import { CommunityPage } from './components/Community/CommunityPage';
import { BillingPage } from './components/Billing/BillingPage';
import { AppLayout } from './components/Layout/AppLayout';
import { useAuth } from './components/Auth/AuthProvider';
import { ChallengeDetail } from './components/Challenge/ChallengeDetail';
import { StandaloneTerminal } from './pages/StandaloneTerminal';
import { OAuthCallback } from './components/Auth/OAuthCallback';
import { AdminLayout } from './components/Admin/AdminLayout';
import { AdminDashboard } from './components/Admin/Dashboard';
import { UserManagement } from './components/Admin/UserManagement';
import { SystemSettingsPanel } from './components/Admin/SystemSettings';
import { AnalyticsDashboard } from './components/Admin/AnalyticsDashboard';
import { AuditLogs } from './components/Admin/AuditLogs';
import { User } from './types';
import ChallengeList from './pages/admin/ChallengeList';
import ChallengeForm from './pages/admin/ChallengeForm';

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading OpsQuest...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{element}</AppLayout>;
};

const AdminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading OpsQuest...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <AppLayout>{element}</AppLayout>;
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();

  // Listen for OAuth completion in other tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      console.log('storage event', e);
      if (e.key === 'oauth_complete') {
        // Try to get the intended redirect page
        const returnTo = localStorage.getItem('oauth_return_to') || '/dashboard';
        window.location.href = returnTo;
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleLoginSuccess = (user: User) => {
    if (user?.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginForm onSuccess={handleLoginSuccess} />} />
      <Route path="/signup" element={<SignupForm onSuccess={() => navigate('/dashboard')} />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute element={<Dashboard />} />} />
      <Route path="/challenges/:id" element={<ProtectedRoute element={<ChallengeDetail />} />} />
      <Route path="/community" element={<ProtectedRoute element={<CommunityPage />} />} />
      <Route path="/billing" element={<ProtectedRoute element={<BillingPage />} />} />
      <Route path="/standalone-terminal" element={<ProtectedRoute element={<StandaloneTerminal />} />} />
      <Route path="/auth/callback" element={<OAuthCallback />} />

      {/* Admin routes */}
      <Route path="/admin" element={<AdminRoute element={<AdminLayout />} />}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="challenges" element={<ChallengeList />} />
        <Route path="challenges/new" element={<ChallengeForm />} />
        <Route path="challenges/:id/edit" element={<ChallengeForm />} />
        <Route path="settings" element={<SystemSettingsPanel />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="billing" element={<div className='text-white p-8'>Billing page coming soon.</div>} />
        <Route path="community" element={<div className='text-white p-8'>Community page coming soon.</div>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
